import { Component, OnInit } from '@angular/core';
import { ContaReceberListaItem, FinContaReceberListaService, ObservacaoContaReceberPayload, OpcaoIdDescricao } from 'src/app/service/fin-conta-receber-lista.service';
import { FinSantanderApiService } from 'src/app/service/fin-santander-api.service';
import { FinSantanderCnabService } from 'src/app/service/fin-santander-cnab.service';
import { FinBanrisulApiService } from 'src/app/service/fin-banrisul-api.service';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { FIN_CONTA_RECEBER_ID_EMPRESA_LISTAGEM } from 'src/app/constants/santander-financeiro.constants';
import { API } from 'src/environments/environment';
import { throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-gerar-boleto',
  templateUrl: './gerar-boleto.component.html',
  styleUrls: ['./gerar-boleto.component.scss']
})
export class GerarBoletoComponent implements OnInit {
  itens: ContaReceberListaItem[] = [];
  itensPaginados: ContaReceberListaItem[] = [];
  loading = false;
  paginaAtual = 1;
  itensPorPagina = 15;

  tiposRecebimento: OpcaoIdDescricao[] = [];

  /** Busca livre pelo nome do pagador */
  filtroNomePagador = '';
  filtroDataEmissaoInicio = '';
  filtroDataEmissaoFim = '';
  filtroDataVencimentoInicio = '';
  filtroDataVencimentoFim = '';
  filtroDataRecebimentoInicio = '';
  filtroDataRecebimentoFim = '';
  filtroIdTipoRecebimento = 0;
  filtroObservacao = '';
  /** T = todos, S = registrado na API Santander, N = ainda não registrado */
  filtroRegistradoApi: 'T' | 'S' | 'N' = 'T';
  /** T = todos, S = boleto confirmado no banco, N = ainda não confirmado */
  filtroBoletoGerado: 'T' | 'S' | 'N' = 'T';
  /** N = apenas em aberto (não pago), T = todos */
  filtroPago: 'N' | 'T' = 'N';

  selecionados = new Set<number>();

  modalObservacaoAberto = false;
  acaoPendente: 'santander' | 'banrisul' | 'preview' | null = null;
  observacoesEdicao: {
    id: number;
    usuarioPagador: string;
    parcela: number;
    valorDocumento: number | null;
    observacao: string;
  }[] = [];

  constructor(
    private finLista: FinContaReceberListaService,
    private ajaxService: AjaxService,
    private util: UtilService,
    private santanderApi: FinSantanderApiService,
    private santanderCnab: FinSantanderCnabService,
    private banrisulApi: FinBanrisulApiService
  ) {}

  ngOnInit(): void {
    this.carregarOpcoes();
    this.carregar();
  }

  get totalPaginas(): number {
    return Math.ceil(this.itens.length / this.itensPorPagina) || 1;
  }

  get todosDaPaginaSelecionados(): boolean {
    return (
      this.itensPaginados.length > 0 &&
      this.itensPaginados.every((i) => this.selecionados.has(i.id))
    );
  }

  carregarOpcoes(): void {
    this.ajaxService.get(`${API}financeiro/FinContaReceber/ConsultarOpcoes`).subscribe({
      next: (response: any) => {
        if (response?.hasErrors) {
          return;
        }
        const data = this.desempacotarResponse(response);
        const tiposData = data.tiposrecebimento || data.tipopagamento || data.tiposPagamento || [];
        this.tiposRecebimento = this.normalizarLista(tiposData)
          .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
          .filter((x) => x.id > 0);
      }
    });
  }

  montarQueryLista(): URLSearchParams {
    const params = new URLSearchParams();
    params.set('idEmpresa', String(FIN_CONTA_RECEBER_ID_EMPRESA_LISTAGEM));
    if (this.filtroPago === 'N') {
      params.set('pago', 'false');
    }
    if (this.filtroBoletoGerado === 'S') {
      params.set('boletoGerado', 'true');
    } else if (this.filtroBoletoGerado === 'N') {
      params.set('boletoGerado', 'false');
    }
    if (this.filtroNomePagador.trim()) {
      params.set('nomePagador', this.filtroNomePagador.trim());
    }
    const [emiIni, emiFim] = this.ordenarParDatas(this.filtroDataEmissaoInicio, this.filtroDataEmissaoFim);
    if (emiIni) params.set('dataEmissaoInicio', emiIni);
    if (emiFim) params.set('dataEmissaoFim', emiFim);
    const [venIni, venFim] = this.ordenarParDatas(this.filtroDataVencimentoInicio, this.filtroDataVencimentoFim);
    if (venIni) params.set('dataVencimentoInicio', venIni);
    if (venFim) params.set('dataVencimentoFim', venFim);
    const [recIni, recFim] = this.ordenarParDatas(this.filtroDataRecebimentoInicio, this.filtroDataRecebimentoFim);
    if (recIni) params.set('dataRecebimentoInicio', recIni);
    if (recFim) params.set('dataRecebimentoFim', recFim);
    if (this.filtroIdTipoRecebimento > 0) {
      params.set('idTipoRecebimento', String(this.filtroIdTipoRecebimento));
    }
    if (this.filtroObservacao.trim()) {
      params.set('observacao', this.filtroObservacao.trim());
    }
    if (this.filtroRegistradoApi === 'S') {
      params.set('boletoRegistradoApi', 'true');
    } else if (this.filtroRegistradoApi === 'N') {
      params.set('boletoRegistradoApi', 'false');
    }
    return params;
  }

  carregar(): void {
    this.loading = true;
    this.util.showLoading();
    this.finLista.carregarLista(this.montarQueryLista()).subscribe({
      next: (lista) => {
        this.itens = lista;
        this.paginaAtual = 1;
        this.selecionados.clear();
        this.atualizarPaginacao();
        this.loading = false;
        this.util.closeLoading();
      },
      error: () => {
        this.itens = [];
        this.itensPaginados = [];
        this.selecionados.clear();
        this.loading = false;
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Não foi possível carregar contas a receber.');
      }
    });
  }

  limparFiltros(): void {
    this.filtroNomePagador = '';
    this.filtroDataEmissaoInicio = '';
    this.filtroDataEmissaoFim = '';
    this.filtroDataVencimentoInicio = '';
    this.filtroDataVencimentoFim = '';
    this.filtroDataRecebimentoInicio = '';
    this.filtroDataRecebimentoFim = '';
    this.filtroIdTipoRecebimento = 0;
    this.filtroObservacao = '';
    this.filtroRegistradoApi = 'T';
    this.filtroBoletoGerado = 'T';
    this.filtroPago = 'N';
    this.carregar();
  }

  toggleSelecionado(id: number): void {
    if (this.selecionados.has(id)) {
      this.selecionados.delete(id);
    } else {
      this.selecionados.add(id);
    }
  }

  toggleTodosPagina(): void {
    if (this.todosDaPaginaSelecionados) {
      this.itensPaginados.forEach((i) => this.selecionados.delete(i.id));
    } else {
      this.itensPaginados.forEach((i) => this.selecionados.add(i.id));
    }
  }

  isSelecionado(id: number): boolean {
    return this.selecionados.has(id);
  }

  /** Consulta liquidação no Santander e aplica baixa automática. */
  consultarPagamentoSantander(): void {
    const ids = Array.from(this.selecionados);
    if (!ids.length) {
      return;
    }
    this.util.showLoading();
    this.santanderApi.consultarBoletos(ids).subscribe({
      next: (resultados) => {
        this.util.closeLoading();
        this.tratarResultadoConsulta(
          resultados.map((r) => ({
            idContaReceber: r.idContaReceber,
            status: r.mensagem || r.statusSantander,
            liquidado: r.liquidado,
            atualizado: r.atualizado,
          })),
          'Santander'
        );
      },
      error: (err: any) => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', err?.message || 'Falha ao consultar pagamento.');
      }
    });
  }

  /** Consulta liquidação no Banrisul e aplica baixa automática. */
  consultarPagamentoBanrisul(): void {
    const ids = Array.from(this.selecionados);
    if (!ids.length) {
      return;
    }
    this.util.showLoading();
    this.banrisulApi
      .obterConfig()
      .pipe(
        switchMap((cfg) => {
          if (!cfg.configurado) {
            return throwError(
              () => new Error('Configure o Banrisul em Contas a Receber → Configuração API Banrisul.')
            );
          }
          return this.banrisulApi.consultarBoletos(ids);
        })
      )
      .subscribe({
        next: (resultados) => {
          this.util.closeLoading();
          this.tratarResultadoConsulta(
            resultados.map((r) => ({
              idContaReceber: r.idContaReceber,
              status: r.mensagem || r.statusBanrisul,
              liquidado: r.liquidado,
              atualizado: r.atualizado,
            })),
            'Banrisul'
          );
        },
        error: (err: any) => {
          this.util.closeLoading();
          this.util.toasterShowError('Erro', err?.message || 'Falha ao consultar pagamento no Banrisul.');
        }
      });
  }

  private tratarResultadoConsulta(
    resultados: { idContaReceber: number; status: string; liquidado: boolean; atualizado: boolean }[],
    banco: string
  ): void {
    const atualizados = resultados.filter((r) => r.atualizado).length;
    const liquidados = resultados.filter((r) => r.liquidado).length;
    const detalhes = resultados.map((r) => `Conta ${r.idContaReceber}: ${r.status}`).join(' | ');
    if (atualizados > 0) {
      this.util.toasterShowSuccess('Liquidação', `${atualizados} título(s) baixado(s) automaticamente (${banco}).`);
      this.selecionados.clear();
      this.carregar();
    } else if (liquidados > 0) {
      this.util.toasterShowSuccess('Consulta', detalhes || `Título(s) já liquidado(s) (${banco}).`);
      this.carregar();
    } else {
      this.util.toasterShowWarning('Consulta', detalhes || `Nenhum pagamento encontrado no ${banco}.`);
    }
  }

  /** Registra boletos na API Santander e abre HTML para impressão. */
  registrarBoletos(): void {
    this.abrirModalObservacao('santander');
  }

  /** Registra boletos na API Banrisul e abre HTML com PDF para impressão. */
  registrarBanrisul(): void {
    this.abrirModalObservacao('banrisul');
  }

  /** Pré-visualização local (não registra no banco). */
  preVisualizarBoletos(): void {
    this.abrirModalObservacao('preview');
  }

  abrirModalObservacao(acao: 'santander' | 'banrisul' | 'preview'): void {
    const ids = Array.from(this.selecionados);
    if (!ids.length) {
      return;
    }

    this.acaoPendente = acao;
    this.observacoesEdicao = ids.map((id) => {
      const item = this.itens.find((i) => i.id === id);
      const obs = (item?.observacao || '').trim();
      return {
        id,
        usuarioPagador: item?.usuarioPagador || '-',
        parcela: item?.parcela || 0,
        valorDocumento: item?.valorDocumento ?? null,
        observacao: obs === '-' ? '' : obs,
      };
    });
    this.modalObservacaoAberto = true;
  }

  fecharModalObservacao(): void {
    this.modalObservacaoAberto = false;
    this.acaoPendente = null;
    this.observacoesEdicao = [];
  }

  get tituloModalObservacao(): string {
    switch (this.acaoPendente) {
      case 'banrisul':
        return 'Observações — registrar no Banrisul';
      case 'preview':
        return 'Observações — pré-visualizar boleto';
      default:
        return 'Observações — registrar no Santander';
    }
  }

  confirmarObservacaoEGerar(): void {
    const observacoes: ObservacaoContaReceberPayload[] = this.observacoesEdicao.map((x) => ({
      idContaReceber: x.id,
      observacao: (x.observacao || '').trim(),
    }));
    const ids = observacoes.map((x) => x.idContaReceber);
    const acao = this.acaoPendente;
    this.fecharModalObservacao();

    if (!acao || !ids.length) {
      return;
    }

    switch (acao) {
      case 'santander':
        this.executarRegistrarSantander(ids, observacoes);
        break;
      case 'banrisul':
        this.executarRegistrarBanrisul(ids, observacoes);
        break;
      case 'preview':
        this.executarPreVisualizar(ids, observacoes);
        break;
    }
  }

  private executarRegistrarSantander(ids: number[], observacoes: ObservacaoContaReceberPayload[]): void {
    this.util.showLoading();
    this.santanderApi
      .obterConfig()
      .pipe(
        switchMap((cfg) => {
          if (!cfg.configurado) {
            return throwError(
              () =>
                new Error(
                  'Configure o Santander em Contas a Receber → Configuração API Santander.'
                )
            );
          }
          if (!cfg.workspaceId) {
            return throwError(
              () => new Error('Crie a workspace na Configuração API Santander antes de registrar boletos.')
            );
          }
          return this.santanderApi.registrarBoletosAbrirGuia(ids, observacoes);
        })
      )
      .subscribe({
        next: () => {
          this.util.closeLoading();
          this.util.toasterShowSuccess(
            'Boleto',
            'Boleto registrado no Santander. Abra a nova guia para visualizar ou imprimir.'
          );
          this.selecionados.clear();
          this.carregar();
        },
        error: (err: any) => {
          this.util.closeLoading();
          const msg = err?.message || 'Falha ao registrar boleto.';
          this.util.toasterShowError('Erro', msg);
        }
      });
  }

  private executarRegistrarBanrisul(ids: number[], observacoes: ObservacaoContaReceberPayload[]): void {
    this.util.showLoading();
    let ambienteCfg = '';
    this.banrisulApi
      .obterConfig()
      .pipe(
        switchMap((cfg) => {
          if (!cfg.configurado) {
            return throwError(
              () =>
                new Error(
                  'Configure o Banrisul em Contas a Receber → Configuração API Banrisul.'
                )
            );
          }
          if (!cfg.codigoBeneficiario && !cfg.codigoBeneficiarioCompleto) {
            return throwError(
              () => new Error('Informe o código do beneficiário na Configuração API Banrisul.')
            );
          }
          ambienteCfg = cfg.apiAmbiente || '';
          return this.banrisulApi.registrarBoletosAbrirGuia(ids, observacoes);
        })
      )
      .subscribe({
        next: () => {
          this.util.closeLoading();
          if (ambienteCfg === 'C') {
            this.util.toasterShowSuccess(
              'Certificação Banrisul',
              'Registro enviado com ambiente T. Na guia aberta, confira se o retorno é 04 (certificação OK).'
            );
          } else {
            this.util.toasterShowSuccess(
              'Boleto',
              'Boleto registrado no Banrisul. Abra a nova guia para visualizar ou imprimir.'
            );
          }
          this.selecionados.clear();
          this.carregar();
        },
        error: (err: any) => {
          this.util.closeLoading();
          const msg = err?.message || 'Falha ao registrar boleto no Banrisul.';
          this.util.toasterShowError('Erro', msg);
        }
      });
  }

  private executarPreVisualizar(ids: number[], observacoes: ObservacaoContaReceberPayload[]): void {
    this.util.showLoading();
    this.santanderApi
      .obterConfig()
      .pipe(
        switchMap((cfg) => {
          if (!cfg.configurado && !cfg.agencia && !cfg.apiClientId) {
            return throwError(
              () =>
                new Error(
                  'Configure o Santander em Contas a Receber → Configuração API Santander.'
                )
            );
          }
          return this.santanderCnab.gerarBoletoDemonstrativoAbrirGuia(ids, observacoes);
        })
      )
      .subscribe({
        next: () => {
          this.util.closeLoading();
          this.util.toasterShowSuccess(
            'Pré-visualização',
            'Abra a nova guia para visualizar. Esta versão não registra o boleto no banco.'
          );
        },
        error: (err: any) => {
          this.util.closeLoading();
          const msg = err?.message || 'Falha ao gerar pré-visualização.';
          this.util.toasterShowError('Erro', msg);
        }
      });
  }

  /** Mock do novo layout V2 com retorno real do banco (não registra título). */
  emularBoletoV2(): void {
    this.util.showLoading();
    this.santanderApi.previewBoletoV2MockAbrirGuia().subscribe({
      next: () => {
        this.util.closeLoading();
        this.util.toasterShowSuccess(
          'Boleto V2 (mock)',
          'Abra a nova guia para validar layout e leitura do código de barras.'
        );
      },
      error: (err: any) => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', err?.message || 'Falha ao emular boleto V2.');
      }
    });
  }

  formatMoney(value: number | null): string {
    if (value == null) {
      return '-';
    }
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  simNao(v: boolean): string {
    return v ? 'Sim' : 'Não';
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.atualizarPaginacao();
    }
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.atualizarPaginacao();
    }
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  /** Garante data inicial ≤ final (formato yyyy-MM-dd do input type=date). */
  private ordenarParDatas(a: string, b: string): [string, string] {
    const aa = (a || '').trim();
    const bb = (b || '').trim();
    if (!aa || !bb) {
      return [aa, bb];
    }
    return aa <= bb ? [aa, bb] : [bb, aa];
  }

  private atualizarPaginacao(): void {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.itensPaginados = this.itens.slice(inicio, fim);
  }

  private desempacotarResponse(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }

  private normalizarLista(data: any): any[] {
    if (!data) {
      return [];
    }
    if (Array.isArray(data)) {
      return data;
    }
    if (typeof data === 'object') {
      if (Object.prototype.hasOwnProperty.call(data, 'id') || Object.prototype.hasOwnProperty.call(data, 'descricao')) {
        return [data];
      }
      return Object.values(data).filter((x: any) => x && typeof x === 'object');
    }
    return [];
  }
}
